/**
 * ClassificationAgentTrigger
 *
 * Small, subtle button that opens the Classification Agent panel.
 * Can be placed anywhere a procurement item is displayed.
 *
 * Usage:
 *   <ClassificationAgentTrigger
 *     itemId={123}
 *     description="Cisco SmartNet..."
 *     supplier="CDW"
 *     amount={50000}
 *     source="sankey"
 *   />
 *
 * Variants:
 *   - "icon" (default): Just the Sparkles icon, minimal footprint
 *   - "compact": Icon + "Agent" text
 *   - "full": Icon + "Ask Agent" text with description
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import { useClassificationAgent } from '../../contexts/ClassificationAgentContext';
import './ClassificationAgentTrigger.css';

function ClassificationAgentTrigger({
  // Required
  itemId,

  // Optional context (passed to agent)
  description,
  supplier,
  amount,
  componentName,
  componentId,
  confidence,
  method,
  documentNumber,
  departmentId,

  // Optional flag context
  flagId,
  flagIds,
  categories,
  hint,

  // Source for analytics
  source = 'other',

  // Display variant
  variant = 'icon',

  // Additional props
  className = '',
  disabled = false,
  title,
}) {
  const { openAgent, isOpen } = useClassificationAgent();

  const handleClick = (e) => {
    e.stopPropagation(); // Don't trigger parent click handlers
    openAgent({
      itemId,
      description,
      supplier,
      amount,
      componentName,
      componentId,
      confidence,
      method,
      documentNumber,
      departmentId,
      flagId,
      flagIds,
      categories,
      hint,
      source,
    });
  };

  const defaultTitle = disabled
    ? 'Agent unavailable'
    : 'Open Classification Agent - dialogue to investigate, fix, and resolve';

  return (
    <button
      type="button"
      className={`cat-trigger cat-trigger--${variant} ${className}`}
      onClick={handleClick}
      disabled={disabled || isOpen}
      title={title || defaultTitle}
    >
      <Sparkles size={variant === 'icon' ? 14 : 12} />
      {variant === 'compact' && <span>Agent</span>}
      {variant === 'full' && <span>Ask Agent</span>}
    </button>
  );
}

export default ClassificationAgentTrigger;
